import type { SendFacts } from "../booking/replayRule";
import { generateIdempotencyKey, isContractValidKey, type FrozenExpectation } from "../booking/attempt";
import type { TermsAcceptance, TermsDecision } from "../terms/terms";

/**
 * ONE GUEST CHECKOUT, ONE KEY — established BEFORE the first code request.
 *
 * Today's flow (`features/booking/attempt.ts`) mints the idempotency key at
 * commit. Guest verification cannot: the draft 1.11 contract binds the email
 * code to `(anonymous uid, idempotencyKey)`, so the key must exist when the
 * code is requested, and the SAME key must then carry the verification, the
 * booking submission, and every same-key check of a lost answer. A key minted
 * later names an attempt that has no proof.
 *
 * So the key belongs to the CHECKOUT, not to a submission. It survives an email
 * change (a new code for the same attempt), a hold that ran out and a new hold
 * (the server compares a key's fingerprint only once a booking exists), and
 * every definite refusal that proves no appointment was written. It is
 * replaced only when the identity it is bound to is gone — and then the old
 * attempt is abandoned, never "carried over".
 *
 * Held in memory only. Never rendered, logged, put in a URL, sent to
 * analytics, or written to storage. See `describeCheckout`.
 */
export interface CheckoutAttempt {
  readonly idempotencyKey: string;
  /** The anonymous Firebase uid the key and every proof are scoped to. */
  readonly uid: string;
}

export function beginCheckout(uid: string): CheckoutAttempt {
  if (!uid) throw new Error("cannot begin a guest checkout without an established identity");
  const idempotencyKey = generateIdempotencyKey();
  if (!isContractValidKey(idempotencyKey)) {
    throw new Error("generated an idempotency key outside the contract's accepted shape");
  }
  return Object.freeze({ idempotencyKey, uid });
}

export function isSameIdentity(attempt: CheckoutAttempt, uid: string | null): boolean {
  return typeof uid === "string" && uid.length > 0 && uid === attempt.uid;
}

/**
 * The email string a code was requested for. Trimmed ONCE, here, and never
 * again: the draft contract requires the booking to send "the string you
 * verified", and its fingerprint hashes raw input, so a second normalisation
 * at submit time is how a verified booking becomes GUEST_CONTACT_UNVERIFIED or
 * IDEMPOTENCY_CONFLICT.
 */
export function verifiedEmailFrom(typed: string): string {
  return typed.trim();
}

/** Exactly what one booking submission sends, every time it sends. */
export interface FrozenGuestBookingInput {
  readonly holdId: string;
  readonly idempotencyKey: string;
  readonly guestContact: { readonly fullName: string; readonly email: string };
  readonly guestContactVerificationId: string;
  /**
   * The Terms the guest agreed to on Review, before the code was sent: part of
   * the frozen submission, so the proof and the Terms are one decision. Absent
   * when none were published. Outside Backend's fingerprint, so a re-ask after
   * a Terms refusal freezes a NEW submission under the SAME key and proof.
   */
  readonly termsAcceptance?: TermsAcceptance;
}

export interface GuestSubmission {
  readonly input: FrozenGuestBookingInput;
  /** What the first send was told to expect. Copied, never the caller's object. */
  readonly expected: FrozenExpectation;
  readonly uid: string;
  /**
   * WHEN IT WENT, recorded at freeze, immediately before the first send (rc.11
   * 1c). Never sent. It decides when a replay's refusal can mean "no booking
   * under this key" (machine.ts, `replayIsDefinite`). Absent: never definite.
   */
  readonly sent?: SendFacts;
}


/**
 * Freeze ONE submission. Called once, immediately before the first dispatch;
 * every later send of THIS submission — a check after a lost answer, a
 * double tap that slipped a latch — resends this object unchanged.
 *
 * A new submission may only be frozen after the server has said, in a way the
 * contract documents as "no appointment", that the previous one did not book.
 * That rule is enforced by the state machine, not here.
 */
export function freezeSubmission(
  attempt: CheckoutAttempt,
  parts: {
    holdId: string;
    fullName: string;
    verifiedEmail: string;
    verificationId: string;
    expected: FrozenExpectation;
    /** The decision the checkout holds. Required: no decision, no submission. */
    terms: TermsDecision;
    /** When it goes (rc.11 1c); the caller sends immediately after. */
    sent?: SendFacts;
  }
): GuestSubmission {
  if (!parts.holdId || !parts.verificationId || !parts.verifiedEmail) {
    throw new Error("cannot freeze a guest submission without a hold, a verified email and its proof");
  }
  const input: FrozenGuestBookingInput = Object.freeze({
    holdId: parts.holdId,
    idempotencyKey: attempt.idempotencyKey,
    guestContact: Object.freeze({ fullName: parts.fullName.trim(), email: parts.verifiedEmail }),
    guestContactVerificationId: parts.verificationId,
    ...(parts.terms.acceptance ? { termsAcceptance: parts.terms.acceptance } : {}),
  });
  return Object.freeze({
    input,
    uid: attempt.uid,
    ...(parts.sent ? { sent: Object.freeze({ ...parts.sent }) } : {}),
    expected: Object.freeze({
      professionalId: parts.expected.professionalId,
      serviceId: parts.expected.serviceId,
      startTime: parts.expected.startTime,
    }),
  });
}

/** The only thing that may be said about a checkout outside this module. */
export function describeCheckout(attempt: CheckoutAttempt | null): string {
  return attempt ? "a guest checkout is in progress" : "no guest checkout";
}
